<script setup lang="ts">
  import { Text } from "../Text";

  const {
    disabled = false,
    isChecked = false,
    label,
    name,
    subtext,
    value,
  } = defineProps<{
    /** Флаг, определяющий, является ли радио кнопка неактивной */
    disabled?: boolean;
    /** Флаг, определяющий, является ли радио кнопка выбранной */
    isChecked?: boolean;
    /** Текст радио кнопки */
    label?: string;
    /** Имя радио кнопки */
    name?: string;
    /** Подтекст радио кнопки */
    subtext?: string;
    /** Значение радио кнопки */
    value: string;
  }>();
  const emit = defineEmits<{
    /** Событие клика по радио кнопке */
    (e: "click", value: string): void;
  }>();

  const handleClick = (event: Event) => {
    if (disabled) {
      event.preventDefault();

      return;
    }

    emit("click", value);
  };
</script>

<template>
  <label :class="['radio-btn', { 'radio-btn--disabled': disabled }]" @click="handleClick">
    <input type="radio" :name="name" :value="value" :checked="isChecked" />
    <div v-if="label || subtext" class="radio-btn__description">
      <Text v-if="label">{{ label }}</Text>
      <Text v-if="subtext" font="body-regular-14" color="secondary">{{ subtext }}</Text>
    </div>
  </label>
</template>

<style scoped>
  @media (hover: hover) {
    .radio-btn:hover::before {
      background-color: var(--sf-control-background-hover);
      border-color: var(--sf-base-border-black);
    }

    .radio-btn:has(input[type="radio"]:checked):hover::before {
      border-color: var(--sf-control-background-active-hover);
    }
  }

  .radio-btn {
    display: inline-block;
    cursor: pointer;
    position: relative;
    min-height: 20px;
    min-width: 20px;
    width: fit-content;
  }

  .radio-btn__description {
    display: flex;
    flex-direction: column;
    margin-left: 28px;
  }

  .radio-btn__description :deep(.text) {
    display: inline-block;
  }

  .radio-btn input[type="radio"] {
    display: none;
  }

  .radio-btn::before {
    content: "";
    display: inline-block;
    width: 20px;
    height: 20px;
    position: absolute;
    top: 1px;
    left: 0;
    border-style: solid;
    border-color: var(--sf-base-border-dark);
    background-color: var(--sf-control-background-default);
    border-width: 1px;
    border-radius: 100%;
    box-sizing: border-box;
  }

  .radio-btn--disabled {
    pointer-events: none;
    cursor: not-allowed;
  }

  .radio-btn--disabled:before {
    background-color: var(--sf-control-background-disabled);
    border-color: var(--sf-base-border-primary);
  }

  .radio-btn:has(input[type="radio"]:checked)::before {
    border-width: 6px;
    border-color: var(--sf-control-background-active-default);
  }

  .radio-btn:has(input[type="radio"]:checked).radio-btn--disabled:before {
    border-color: var(--sf-control-background-active-disabled);
  }
</style>
